import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtEur, fmtDate } from "../lib/utils";
import { ArrowsLeftRight, Funnel, ArrowDown, ArrowUp, Calendar, MagicWand, CaretDown, CaretUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";

export default function AdminTransactions() {
  const { t, lang } = useI18n();
  const [txs, setTxs] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualForm, setManualForm] = useState({ client_id: "", payment_intent_id: "", amount: "", note: "" });
  const [manualSubmitting, setManualSubmitting] = useState(false);

  const reload = () => {
    api.get("/admin/clients").then(({ data }) => setClients(data));
    api.get("/admin/transactions").then(({ data }) => setTxs(data));
  };

  useEffect(() => { reload(); }, []);

  const filtered = clientFilter ? txs.filter((t) => t.client_id === clientFilter) : txs;
  const clientName = (id) => clients.find((c) => c.id === id)?.company_name || "—";

  const credits = filtered.filter((x) => x.direction === "credit").reduce((s, x) => s + x.amount, 0);
  const debits  = filtered.filter((x) => x.direction === "debit").reduce((s, x) => s + x.amount, 0);

  const reconcilePayments = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/admin/payments/reconcile");
      if (data.fixed.length === 0) {
        toast.success(`Nessun pagamento in sospeso su ${data.checked} controllati`);
      } else {
        const total = data.fixed.reduce((s, f) => s + (f.amount || 0), 0);
        toast.success(`Accreditati ${data.fixed.length} pagament${data.fixed.length === 1 ? "o" : "i"} (${fmtEur(total)}) su ${data.checked} controllati`);
        reload();
      }
      if (data.errors?.length) toast.error(`${data.errors.length} sessioni non verificabili su Stripe`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la riconciliazione");
    } finally {
      setReconciling(false);
    }
  };

  const submitManualCredit = async (e) => {
    e.preventDefault();
    if (!manualForm.client_id || !manualForm.payment_intent_id.trim() || !manualForm.amount) {
      toast.error("Cliente, PaymentIntent e importo sono obbligatori");
      return;
    }
    setManualSubmitting(true);
    try {
      const { data } = await api.post("/admin/payments/mark-credited", {
        client_id: manualForm.client_id,
        payment_intent_id: manualForm.payment_intent_id.trim(),
        amount: parseFloat(manualForm.amount),
        note: manualForm.note || undefined,
      });
      if (data.already_reconciled) {
        toast.info("Questo PaymentIntent era già riconciliato in precedenza - nessuna riga duplicata creata");
      } else {
        toast.success("Storico allineato: nessuna modifica al saldo wallet, solo transazione registrata");
        setManualForm({ client_id: "", payment_intent_id: "", amount: "", note: "" });
        reload();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la riconciliazione manuale");
    } finally {
      setManualSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-transactions">
      <PageHeader
        eyebrow="Movimenti"
        title="Wallet ledger"
        subtitle="Ogni ricarica, ogni addebito, ogni rettifica. Consolidato su tutti i wallet."
        chip="tf-chip-sky"
        icon={ArrowsLeftRight}
        actions={
          <button onClick={reconcilePayments} disabled={reconciling} className="tf-btn tf-btn-ghost" data-testid="wallet-reconcile-payments-btn">
            <MagicWand size={16} weight="fill" /> {reconciling ? "Verifica in corso…" : "Riconcilia Pagamenti Stripe"}
          </button>
        }
      />

      <div className="tf-card p-4 md:p-5">
        <button
          onClick={() => setShowManual((v) => !v)}
          className="flex items-center justify-between w-full text-left"
          data-testid="toggle-manual-reconcile"
        >
          <div>
            <div className="font-display font-bold text-sm">Allinea storico pagamento Stripe già accreditato a mano</div>
            <div className="text-xs text-[var(--tf-muted)] mt-0.5">Registra la riga di storico per un PaymentIntent riuscito senza toccare il saldo del wallet (già aggiunto manualmente).</div>
          </div>
          {showManual ? <CaretUp size={18} weight="bold" /> : <CaretDown size={18} weight="bold" />}
        </button>
        {showManual && (
          <form onSubmit={submitManualCredit} className="grid md:grid-cols-2 gap-3 mt-4 pt-4 border-t border-[var(--tf-border)]">
            <div>
              <label className="tf-label block mb-1">Cliente</label>
              <select
                value={manualForm.client_id}
                onChange={(e) => setManualForm((f) => ({ ...f, client_id: e.target.value }))}
                className="tf-input w-full"
                data-testid="manual-reconcile-client"
              >
                <option value="">Seleziona cliente…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name} ({c.contact_email})</option>)}
              </select>
            </div>
            <div>
              <label className="tf-label block mb-1">PaymentIntent Stripe (pi_...)</label>
              <input
                value={manualForm.payment_intent_id}
                onChange={(e) => setManualForm((f) => ({ ...f, payment_intent_id: e.target.value }))}
                className="tf-input w-full"
                placeholder="pi_..."
                data-testid="manual-reconcile-pi"
              />
            </div>
            <div>
              <label className="tf-label block mb-1">Importo (€)</label>
              <input
                type="number" step="0.01" min="0"
                value={manualForm.amount}
                onChange={(e) => setManualForm((f) => ({ ...f, amount: e.target.value }))}
                className="tf-input w-full"
                data-testid="manual-reconcile-amount"
              />
            </div>
            <div>
              <label className="tf-label block mb-1">Nota (opzionale)</label>
              <input
                value={manualForm.note}
                onChange={(e) => setManualForm((f) => ({ ...f, note: e.target.value }))}
                className="tf-input w-full"
                placeholder="es. Accredito già effettuato manualmente il..."
                data-testid="manual-reconcile-note"
              />
            </div>
            <div className="md:col-span-2">
              <button type="submit" disabled={manualSubmitting} className="tf-btn tf-btn-primary" data-testid="manual-reconcile-submit">
                {manualSubmitting ? "Verifica su Stripe…" : "Segna come già accreditato"}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <div className="tf-card p-3 md:p-5 flex items-center gap-2.5 md:gap-4 min-w-0">
          <div className="tf-chip tf-chip-mint flex-shrink-0"><ArrowsLeftRight size={18} weight="fill" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] md:text-[11px] uppercase tracking-widest text-[var(--tf-muted)]">Accrediti</div>
            <div className="font-display font-black text-sm md:text-xl tf-numeric text-[var(--tf-success)] truncate">+{fmtEur(credits)}</div>
          </div>
        </div>
        <div className="tf-card p-3 md:p-5 flex items-center gap-2.5 md:gap-4 min-w-0">
          <div className="tf-chip tf-chip-peach flex-shrink-0"><ArrowsLeftRight size={18} weight="fill" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] md:text-[11px] uppercase tracking-widest text-[var(--tf-muted)]">Addebiti</div>
            <div className="font-display font-black text-sm md:text-xl tf-numeric text-[var(--tf-error)] truncate">−{fmtEur(debits)}</div>
          </div>
        </div>
        <div className="tf-card p-3 md:p-5 flex items-center gap-2.5 md:gap-4 min-w-0 col-span-2 md:col-span-1">
          <div className="tf-chip tf-chip-lime flex-shrink-0"><ArrowsLeftRight size={18} weight="fill" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] md:text-[11px] uppercase tracking-widest text-[var(--tf-muted)]">Movimenti</div>
            <div className="font-display font-black text-sm md:text-xl tf-numeric truncate">{filtered.length}</div>
          </div>
        </div>
      </div>

      <div className="tf-card p-4 flex gap-3 items-end">
        <div className="tf-chip tf-chip-cream !w-10 !h-10 !rounded-xl flex-shrink-0">
          <Funnel size={16} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <label className="tf-label block mb-1">{t("clients")}</label>
          <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="tf-input w-full" data-testid="txfilter-client">
            <option value="">{t("all")}</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
      </div>
      <div className="tf-card overflow-hidden hidden md:block">
        <div className="overflow-x-auto scroll-thin">
          <table className="tf-table">
            <thead><tr><th>{t("date")}</th><th>{t("clients")}</th><th>{t("type")}</th><th>{t("description")}</th><th className="text-right">{t("amount")}</th><th className="text-right">Balance</th></tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={6} className="text-center text-[var(--tf-muted)] py-10">{t("empty")}</td></tr>}
              {filtered.map((x) => (
                <tr key={x.id}>
                  <td className="text-xs">{fmtDate(x.created_at, lang)}</td>
                  <td>{clientName(x.client_id)}</td>
                  <td className="text-xs uppercase tracking-wider">{x.type}</td>
                  <td className="text-xs">{x.description}</td>
                  <td className={`text-right tf-numeric font-bold ${x.direction === "credit" ? "text-[var(--tf-success)]" : "text-[var(--tf-error)]"}`}>
                    {x.direction === "credit" ? "+" : "−"}{fmtEur(x.amount)}
                  </td>
                  <td className="text-right tf-numeric">{fmtEur(x.balance_after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden tf-mlist" data-testid="admin-tx-mlist">
        {filtered.length === 0 && (
          <div className="tf-card p-8 text-center text-[var(--tf-muted)] text-sm">
            <ArrowsLeftRight size={28} weight="duotone" className="mx-auto mb-2 opacity-60" />
            {t("empty")}
          </div>
        )}
        {filtered.map((x) => {
          const isCredit = x.direction === "credit";
          return (
            <div key={x.id} className="tf-mrow" data-testid={`admin-tx-card-${x.id}`}>
              <div className="tf-mrow-icon" style={{ background: isCredit ? "#D1F0E0" : "#FFE7D3", color: isCredit ? "#1F6E4A" : "#A54B0F" }}>
                {isCredit ? <ArrowDown size={20} weight="bold" /> : <ArrowUp size={20} weight="bold" />}
              </div>
              <div className="tf-mrow-body">
                <div className="tf-mrow-title truncate">{clientName(x.client_id)}</div>
                <div className="tf-mrow-sub truncate">{x.description || x.type}</div>
                <div className="tf-mrow-sub flex items-center gap-1.5 mt-0.5">
                  <Calendar size={11} weight="bold" />
                  {fmtDate(x.created_at, lang)}
                </div>
              </div>
              <div className={`tf-mrow-value tf-numeric ${isCredit ? "text-[var(--tf-success)]" : "text-[var(--tf-error)]"}`}>
                {isCredit ? "+" : "−"}{fmtEur(x.amount)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
